import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Calendar, MapPin, Clock, User, LogOut, FileText, CheckCircle, XCircle,
  Home, Users, Mic2, Map, QrCode, BookOpen, Bell, ChevronRight,
  Star, Download, ExternalLink, Wifi, Coffee, MessageSquare, Award,
  Timer, Heart, Shield, Sun, Moon, Sparkles, Lock, Eye, X, UserCog
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { toPng } from 'html-to-image';
import ChatBot from '../components/ChatBot';
import ShareFlyerWidget from '../components/ShareFlyerWidget';
import EditProfileModal from '../components/EditProfileModal';
import PartnerPortalSection from '../components/PartnerPortalSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Conférence Data
  const conférenceData = {
  en: {
    nav: {
      overview: "Overview",
      schedule: "Schedule",
      speakers: "Speakers",
      venue: "Venue",
      badge: "My Badge",
      resources: "Resources",
      documents: "My Documents",
      notifications: "Notifications",
      history: "Event History",
      partner: "Partner Portal",
    },
    overview: {
      welcome: "Welcome back",
      countdown: "Conférence starts in",
      days: "Days",
      hours: "Hours",
      minutes: "Minutes",
      seconds: "Seconds",
      regStatus: "Registration Status",
      pending: "Pending Review",
      approved: "Approved",
      rejected: "Rejected",
      incomplete: "Incomplete",
      paid: "Paid",
      registerNow: "Register for Conférence",
      notRegistered: "You haven't registered yet",
      pendingMsg: "Your registration is under review. We'll notify you soon.",
      approvedMsg: "Your registration has been approved! Please complete your payment to unlock the full conférence program.",
      paidMsg: "Payment confirmed! You have full access to speakers and program. Your visa invitation letter will be sent within 5 business days.",
      rejectedMsg: "Your registration was not approved. Contact us for détails.",
      incompleteMsg: "Your registration has been returned for complétion. Please review the admin's note below, update your information, and resubmit.",
      editResubmit: "Edit & Resubmit",
      adminNote: "Admin Note",
      payNow: "Pay $499 USD",
      payStripe: "Pay $499 USD Now",
      payStripeSub: "",
      payingMsg: "Redirecting to payment...",
      verifyPayment: "Verify Payment",
      quickActions: "Quick Actions",
      viewSchedule: "View Schedule",
      downloadApp: "Download Program",
      contactOrg: "Contact Organizers",
      announcements: "Announcements",
      conférenceTitle: "Ottawa International Conférence 2027",
      conférenceTheme: "HOW TO RECOVER AFTER: ABUSE, RAPE, HARASSMENT, MISTREATMENT, AND TRAUMA",
      conférenceDate: "March 12-14, 2027",
      conférenceVenue: "Sheraton Hotel, 150 Albert St, Ottawa",
    },
    schedule: {
      title: "Conférence Schedule",
      subtitle: "3 days of powerful sessions, workshops, and networking",
      day1: "Day 1 - Mar 12",
      day2: "Day 2 - Mar 13",
      day3: "Day 3 - Mar 14",
      addToSchedule: "Add to My Schedule",
      added: "Added",
      mySchedule: "My Schedule",
      noSessions: "No sessions added yet. Browse the schedule to add sessions.",
      locked: "The conférence program is currently locked by the organizers. It will be unlocked soon once the schedule is finalized.",
    },
    speakers: {
      title: "Featured Speakers",
      subtitle: "Meet the inspiring women leading our sessions",
      keynote: "Keynote Speaker",
      panelist: "Panelist",
      workshop: "Workshop Leader",
      locked: "This section is currently locked by the organizers. It will be unlocked soon once all speakers are confirmed.",
    },
    venue: {
      title: "Venue Information",
      subtitle: "Everything you need to know about the conférence location",
      hotelName: "Sheraton Ottawa Hotel",
      address: "150 Albert St, Ottawa, ON K1P 5G2, Canada",
      directions: "Getting There",
      parking: "Parking",
      accommodation: "Accommodation",
      nearby: "Nearby Amenities",
      directionsText: "Located in the heart of downtown Ottawa, easily accessible by public transit (OC Transpo). 15 minutes from Ottawa International Airport.",
      parkingText: "On-site underground parking available. Rate: $35/day for conférence attendees. Reserve in advance.",
      accommodationText: "Special group rate at Sheraton Ottawa Hotel: $159 USD ($219 CAD) per night. Book before February 10, 2027 to secure the conférence rate.",
      accommodationBook: "Book Hotel Room",
      accommodationDeadline: "Last Day to Book: February 10, 2027",
      accommodationRate: "$159 USD",
      accommodationRateCad: "($219 CAD)",
      accommodationEventDates: "Mar 11 - 15, 2027",
      nearbyText: "Walking distance to Parliament Hill, Rideau Canal, ByWard Market, and numerous restaurants.",
      wifiName: "SHERATON-CONF",
      wifiPassword: "TALITHA2026",
    },
    badge: {
      title: "My Conférence Badge",
      subtitle: "Show this QR code at registration for quick check-in",
      attendee: "ATTENDEE",
      scanInfo: "Present this QR code at the entrance for contactless check-in",
      downloadBadge: "Download Badge",
      conférenceId: "Conférence ID",
    },
    resources: {
      title: "Conférence Resources",
      subtitle: "Download materials and prepare for the conférence",
      programGuide: "Conférence Program Guide",
      programDesc: "Complete schedule, speaker bios, and session details",
      preparationKit: "Attendee Préparation Kit",
      prepDesc: "What to bring, dress code, and helpful tips",
      safetyGuidelines: "Health & Safety Guidelines",
      safetyDesc: "COVID protocols, emergency procedures, and first aid info",
      cityGuide: "Ottawa City Guide",
      cityDesc: "Top attractions, restaurants, and transportation in Ottawa",
      download: "Download",
      comingSoon: "Available Mar 1",
      noResources: "No resources uploaded yet. Check back closer to the conférence date.",
    },
    documents: {
      title: "My Documents",
      subtitle: "Personal documents shared with you by the organizers",
      noDocuments: "No documents have been shared with you yet.",
      download: "Download",
      preview: "Preview",
      from: "From organizers",
      previewError: "Unable to load preview",
    },
    notifications: {
      title: "Notifications",
      subtitle: "Stay updated with the latest conférence news",
      noNotifications: "No notifications yet.",
      markAllRead: "Mark all as read",
      new: "New",
    },
    logout: "Sign Out",
    editProfile: "Edit Profile",
  },
  fr: {
    nav: {
      overview: "Aperçu",
      schedule: "Programme",
      speakers: "Conférenciers",
      venue: "Lieu",
      badge: "Mon badge",
      resources: "Ressources",
      documents: "Mes documents",
      notifications: "Notifications",
      history: "Historique",
      partner: "Espace partenaire",
    },
    overview: {
      welcome: "Bienvenue",
      countdown: "La conférence commence dans",
      days: "Jours",
      hours: "Heures",
      minutes: "Minutes",
      seconds: "Secondes",
      regStatus: "Statut d'Inscription",
      pending: "En attente",
      approved: "Approuvée",
      rejected: "Rejetée",
      incomplete: "Incomplète",
      paid: "Payée",
      registerNow: "S'inscrire à la Conférence",
      notRegistered: "Vous n'êtes pas encore inscrit(e)",
      pendingMsg: "Votre inscription est en cours de révision. Nous vous informerons bientôt.",
      approvedMsg: "Votre inscription a été approuvée! Veuillez completer votre paiement pour débloquer le programme complet de la conférence.",
      paidMsg: "Paiement confirmé! Vous avez un accès complet aux conférenciers et au programme. Votre lettre d'invitation visa sera envoyée dans les 5 jours ouvrables.",
      rejectedMsg: "Votre inscription n'a pas été approuvée. Contactez-nous pour plus de détails.",
      incompleteMsg: "Votre inscription a été retournée pour complétion. Veuillez consulter la note de l'admin ci-dessous, mettre à jour vos informations et resoumettre.",
      editResubmit: "Modifier et Resoumettre",
      adminNote: "Note de l'Administrateur",
      payNow: "Payer 499 $ USD (Square)",
      payStripe: "Payer 499 $ USD Maintenant",
      payStripeSub: "Recommandé si vous êtes hors d'Amérique du Nord",
      payingMsg: "Redirection vers le paiement...",
      verifyPayment: "Vérifier le Paiement",
      quickActions: "Actions rapides",
      viewSchedule: "Voir le Programme",
      downloadApp: "Télécharger le Programme",
      contactOrg: "Contacter les Organisateurs",
      announcements: "Annonces",
      conférenceTitle: "Conférence Internationale Ottawa 2027",
      conférenceTheme: "COMMENT SE RELEVER APRÈS: ABUS, VIOL, HARCÈLEMENT, MALTRAITANCE ET TRAUMATISME",
      conférenceDate: "12-14 Mars 2027",
      conférenceVenue: "Hôtel Sheraton, 150 Albert St, Ottawa",
    },
    schedule: {
      title: "Programme de la Conférence",
      subtitle: "3 jours de sessions puissantes, ateliers et réseautage",
      day1: "Jour 1 - 12 Mars",
      day2: "Jour 2 - 13 Mars",
      day3: "Jour 3 - 14 Mars",
      addToSchedule: "Ajouter à Mon Programme",
      added: "Ajouté",
      mySchedule: "Mon Programme",
      noSessions: "Aucune session ajoutée. Parcourez le programme pour en ajouter.",
      locked: "Le programme de la conférence est actuellement verrouillé par les organisateurs. Il sera déverrouillé bientôt une fois le programme finalisé.",
    },
    speakers: {
      title: "Conférenciers en Vedette",
      subtitle: "Rencontrez les femmes inspirantes qui dirigent nos sessions",
      keynote: "Conférencière Principale",
      panelist: "Panéliste",
      workshop: "Animatrice d'Atelier",
      locked: "Cette section est actuellement verrouillée par les organisateurs. Elle sera déverrouillée bientôt une fois tous les conférenciers confirmés.",
    },
    venue: {
      title: "Informations sur le Lieu",
      subtitle: "Tout ce que vous devez savoir sur le lieu de la conférence",
      hotelName: "Hôtel Sheraton Ottawa",
      address: "150 Albert St, Ottawa, ON K1P 5G2, Canada",
      directions: "Comment s'y Rendre",
      parking: "Stationnement",
      accommodation: "Hébergement",
      nearby: "À Proximité",
      directionsText: "Situé au cœur du centre-ville d'Ottawa, facilement accessible en transport en commun (OC Transpo). À 15 minutes de l'aéroport international d'Ottawa.",
      parkingText: "Stationnement souterrain sur place disponible. Tarif: 35$/jour pour les participants. Réservez à l'avance.",
      accommodationText: "Tarif de groupe spécial au Sheraton Ottawa Hotel: 159 $ USD (219 $ CAN) par nuit. Réservez avant le 10 février 2027 pour bénéficier du tarif conférence.",
      accommodationBook: "Réserver une Chambre",
      accommodationDeadline: "Date limite de réservation: 10 février 2027",
      accommodationRate: "159 $ USD",
      accommodationRateCad: "(219 $ CAN)",
      accommodationEventDates: "11 - 15 mars 2027",
      nearbyText: "À distance de marche de la Colline du Parlement, du Canal Rideau, du Marché By et de nombreux restaurants.",
      wifiName: "SHERATON-CONF",
      wifiPassword: "TALITHA2026",
    },
    badge: {
      title: "Mon badge de conférence",
      subtitle: "Montrez ce code QR à l'inscription pour un enregistrement rapide",
      attendee: "PARTICIPANT",
      scanInfo: "Présentez ce code QR à l'entrée pour un enregistrement sans contact",
      downloadBadge: "Télécharger le Badge",
      conférenceId: "ID Conférence",
    },
    resources: {
      title: "Ressources de la Conférence",
      subtitle: "Téléchargez les documents et préparez-vous pour la conférence",
      programGuide: "Guide du Programme",
      programDesc: "Programme complet, biographies des conférenciers et détails des sessions",
      preparationKit: "Kit de Préparation",
      prepDesc: "Quoi apporter, code vestimentaire et conseils utiles",
      safetyGuidelines: "Consignes de Santé et Sécurité",
      safetyDesc: "Protocoles COVID, procédures d'urgence et premiers soins",
      cityGuide: "Guide de la Ville d'Ottawa",
      cityDesc: "Meilleures attractions, restaurants et transports à Ottawa",
      download: "Télécharger",
      comingSoon: "Disponible 1er Oct",
      noResources: "Aucune ressource n'a été publiée. Revenez plus tard.",
    },
    documents: {
      title: "Mes documents",
      subtitle: "Documents personnels partagés par les organisateurs",
      noDocuments: "Aucun document ne vous a encore été partagé.",
      download: "Télécharger",
      preview: "Aperçu",
      from: "Des organisateurs",
      previewError: "Impossible de charger l'aperçu",
    },
    notifications: {
      title: "Notifications",
      subtitle: "Restez informé(e) des dernières nouvelles de la conférence",
      noNotifications: "Aucune notification pour le moment.",
      markAllRead: "Tout marquer comme lu",
      new: "Nouveau",
    },
    logout: "Déconnexion",
    editProfile: "Modifier mon profil",
  }
};

// Agenda mirrors /app/backend/program_pdf.py FALLBACK_AGENDA so the Program tab
// and the downloaded PDF always show the exact same schedule.
const scheduleData = [
  // Day 1 — Thursday, March 12, 2027
  [
    { id: 's1', time: '08:30 – 09:30', title: { en: 'Welcome & Check-in', fr: 'Accueil & Enregistrement' }, type: 'general', speaker: 'Équipe TKTUG', icon: Coffee },
    { id: 's2', time: '09:30 – 10:30', title: { en: 'Opening Ceremony', fr: 'Cérémonie d\'ouverture' }, type: 'keynote', speaker: 'Nadège Tankama, Présidente', icon: Mic2 },
    { id: 's3', time: '10:45 – 12:15', title: { en: 'Keynote: Recovering after Trauma', fr: 'Conférence : Se relever après un traumatisme' }, type: 'keynote', speaker: 'Dr. Sara Mubaya', icon: Mic2 },
    { id: 's4', time: '12:30 – 13:45', title: { en: 'Lunch & Networking', fr: 'Déjeuner & Réseautage' }, type: 'general', icon: Coffee },
    { id: 's5', time: '14:00 – 16:00', title: { en: 'Workshop: Rebuilding Self-Esteem', fr: 'Atelier : Reconstruire l\'estime de soi' }, type: 'workshop', speaker: 'Alicia Bwenge, MA', icon: Heart },
    { id: 's6', time: '16:15 – 17:30', title: { en: 'Panel: Voices of Survivors', fr: 'Table ronde : Voix des survivantes' }, type: 'panel', speaker: 'Amanda Kabongo (modératrice)', icon: Users },
    { id: 's7', time: '18:30 – 20:30', title: { en: 'Gala Dinner', fr: 'Dîner de gala' }, type: 'social', icon: Star },
  ],
  // Day 2 — Friday, March 13, 2027
  [
    { id: 's8', time: '08:30 – 09:15', title: { en: 'Morning Coffee', fr: 'Café d\'accueil' }, type: 'general', icon: Coffee },
    { id: 's9', time: '09:15 – 10:45', title: { en: 'Keynote: Forgiveness as Freedom', fr: 'Conférence : Le pardon comme libération' }, type: 'keynote', speaker: 'Rév. Grace Mwamba', icon: Mic2 },
    { id: 's10', time: '11:00 – 12:30', title: { en: 'Workshop: Economic Empowerment', fr: 'Atelier : Autonomisation économique' }, type: 'workshop', speaker: 'Padre Louis Mukanya', icon: Award },
    { id: 's11', time: '12:30 – 13:45', title: { en: 'Lunch', fr: 'Déjeuner' }, type: 'general', icon: Coffee },
    { id: 's12', time: '14:00 – 15:30', title: { en: 'Country Support Groups', fr: 'Groupes de soutien par pays' }, type: 'session', speaker: 'Coordinatrices régionales', icon: Users },
    { id: 's13', time: '15:45 – 17:15', title: { en: 'Workshop: Public Speaking', fr: 'Atelier : Prendre la parole en public' }, type: 'workshop', speaker: 'Mama Ruth Tshiala', icon: MessageSquare },
    { id: 's14', time: '18:00 – 20:00', title: { en: 'Cultural Evening', fr: 'Soirée culturelle' }, type: 'social', speaker: 'Équipe TKTUG', icon: Sparkles },
  ],
  // Day 3 — Saturday, March 14, 2027
  [
    { id: 's15', time: '09:00 – 10:30', title: { en: 'Closing Keynote', fr: 'Message de clôture' }, type: 'keynote', speaker: 'Nadège Tankama, Présidente', icon: Mic2 },
    { id: 's16', time: '10:45 – 12:15', title: { en: 'Workshop Reports & Commitments', fr: 'Rapports des ateliers & Engagements' }, type: 'session', speaker: 'Équipe TKTUG', icon: BookOpen },
    { id: 's17', time: '12:30 – 13:45', title: { en: 'Lunch', fr: 'Déjeuner' }, type: 'general', icon: Coffee },
    { id: 's18', time: '14:00 – 15:30', title: { en: 'Certificate Presentation', fr: 'Remise des certificats' }, type: 'keynote', speaker: 'Nadège Tankama', icon: Award },
    { id: 's19', time: '15:30 – 16:30', title: { en: 'Official Photos & Farewell', fr: 'Photos officielles & Au revoir' }, type: 'social', icon: Sparkles },
  ],
];

const speakersData = [
  {
    name: 'Dr. Marie-Claire Kabongo',
    role: { en: 'Keynote Speaker', fr: 'Conférencière Principale' },
    bio: { en: 'Renowned psychologist specializing in trauma recovery with 20+ years of experience working with abuse survivors across Africa and North America.', fr: 'Psychologue renommée spécialisée dans la guérison des traumatismes avec plus de 20 ans d\'experience aupres des survivantes d\'abus en Afrique et en Amérique du Nord.' },
    topic: { en: 'Rising After the Storm', fr: 'Se Relever Après la Tempête' },
    image: 'https://images.unsplash.com/photo-1666867936058-de34bfd5b320?w=400&h=400&fit=crop&crop=face',
  },
  {
    name: 'Dr. Amina Ouedraogo',
    role: { en: 'Keynote Speaker', fr: 'Conférencière Principale' },
    bio: { en: 'Clinical therapist and author of "Reclaiming Your Voice", expert in cognitive behavioral therapy for trauma survivors.', fr: 'Thérapeute clinicienne et auteure de "Reclaimer Votre Voix", experte en thérapie cognitive comportementale pour les survivantes de traumatismes.' },
    topic: { en: 'Rebuilding Self-Worth After Trauma', fr: 'Reconstruire l\'Estime de Soi Apres un Traumatisme' },
    image: 'https://images.unsplash.com/photo-1607990283143-e81e7a2c9349?w=400&h=400&fit=crop&crop=face',
  },
  {
    name: 'Aminata Diallo',
    role: { en: 'Workshop Leader', fr: 'Animatrice d\'Atelier' },
    bio: { en: 'Art therapist and healing facilitator. Founder of "Colors of Courage" program helping survivors express and process trauma through creative arts.', fr: 'Art-thérapeute et facilitatrice de guérison. Fondatrice du programme "Couleurs du Courage" aidant les survivantes à exprimer et traiter le traumatisme par les arts créatifs.' },
    topic: { en: 'Healing Through Art & Expression', fr: 'Guerison par l\'Art et l\'Expression' },
    image: 'https://images.unsplash.com/photo-1643488448207-cea4ca335830?w=400&h=400&fit=crop&crop=face',
  },
  {
    name: 'Esperance Mukamana',
    role: { en: 'Keynote Speaker', fr: 'Conférencière Principale' },
    bio: { en: 'Survivor advocate and international speaker who has shared her story of resilience on stages across 30+ countries.', fr: 'Defenseure des survivantes et conférenciere internationale qui a partagé son histoire de résilience sur des scènes de plus de 30 pays.' },
    topic: { en: 'From Victim to Victor', fr: 'De Victime à Victorieuse' },
    image: 'https://images.unsplash.com/photo-1642521684111-d5464d8fef88?w=400&h=400&fit=crop&crop=face',
  },
  {
    name: 'Grace Nkemba',
    role: { en: 'Workshop Leader', fr: 'Animatrice d\'Atelier' },
    bio: { en: 'Financial literacy advocate and social entrepreneur dedicated to helping women rebuild their economic independence after abusive situations.', fr: 'Défenseure de la littératie financière et entrepreneure sociale dédiée à aider les femmes a reconstruire leur indépendance économique.' },
    topic: { en: 'Financial Independence After Abuse', fr: 'Indépendance Financière Après les Abus' },
    image: 'https://images.unsplash.com/photo-1552772105-a16e44968cb0?w=400&h=400&fit=crop&crop=face',
  },
];

const announcementsData = [
  { id: 1, date: '2026-04-15', text: { en: 'Early bird registration is now open! Secure your spot today.', fr: 'L\'inscription anticipée est maintenant ouverte! Réservez votre place aujourd\'hui.' }, type: 'info' },
  { id: 2, date: '2026-04-10', text: { en: 'Speaker lineup confirmed! Check the Speakers tab for détails.', fr: 'Liste des conférenciers confirmee! Consultez l\'onglet Conférenciers.' }, type: 'success' },
  { id: 3, date: '2026-04-05', text: { en: 'Special hotel rate available with code TALITHA2026. Book now!', fr: 'Tarif spécial hôtel disponible avec le code TALITHA2026. Réservez maintenant!' }, type: 'promo' },
];

// Countdown Hook
const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  useEffect(() => {
    const tick = () => {
      const diff = new Date(targetDate) - new Date();
      if (diff <= 0) { setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 }); return; }
      setTimeLeft({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);
  return timeLeft;
};

// Session type colors
const typeColors = {
  keynote: 'bg-[#CD0000]/10 text-[#CD0000] border-[#CD0000]/30',
  panel: 'bg-blue-50 text-blue-700 border-blue-200',
  workshop: 'bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30',
  session: 'bg-purple-50 text-purple-700 border-purple-200',
  general: 'bg-gray-50 text-gray-600 border-gray-200',
  social: 'bg-amber-50 text-amber-700 border-amber-200',
  wellness: 'bg-teal-50 text-teal-700 border-teal-200',
};

const getSessionIcon = (type) => {
  const icons = { keynote: Mic2, panel: Users, workshop: BookOpen, session: MessageSquare, general: Coffee, social: Sparkles, wellness: Sun };
  return icons[type] || Calendar;
};

const Dashboard = () => {
  const { language } = useLanguage();
  const { user, token, logout, updateUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [activeDay, setActiveDay] = useState(0);
  const [mySessions, setMySessions] = useState([]);
  const [showMySchedule, setShowMySchedule] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [myDocuments, setMyDocuments] = useState([]);
  const [resources, setResources] = useState([]);
  const [apiSpeakers, setApiSpeakers] = useState([]);
  const [apiSessions, setApiSessions] = useState([]);
  const [speakersLocked, setSpeakersLocked] = useState(true);
  const [programLocked, setProgramLocked] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const [cadInfo, setCadInfo] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null); // {id, title, filename, url}
  const [editProfileOpen, setEditProfileOpen] = useState(false);

  const t = conférenceData[language] || conférenceData.fr;
  const countdown = useCountdown('2027-03-12T08:00:00');

  useEffect(() => {
    if (authLoading) return; // wait for AuthContext to hydrate from localStorage
    if (!user) { navigate('/login'); return; }
    if (user.role === 'admin' || user.role === 'sub_admin') { navigate('/admin'); return; }
    fetchRegistration();
    fetchSchedule();
    fetchUnreadCount();
    fetchDocuments();
    fetchResources();
    fetchSpeakersAndSessions();
    // Check if returning from payment
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment_complete') === '1') {
      handleVerifyPayment();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    // Debounced save to backend when sessions change
    if (!token || mySessions.length === 0) return;
    const timer = setTimeout(() => {
      axios.post(`${BACKEND_URL}/api/schedule/save`, { sessions: mySessions }, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    }, 1000);
    return () => clearTimeout(timer);
  }, [mySessions, token]);

  const fetchRegistration = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/registrations/my-registration`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegistration(response.data);
    } catch (err) { /* No registration */ }
    finally { setLoading(false); }
  };

  const fetchSchedule = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/schedule/mine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.sessions?.length) setMySessions(response.data.sessions);
    } catch (err) {}
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.count);
    } catch (err) {}
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (err) {}
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/participations/my-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHistoryData(response.data);
    } catch (err) {}
  };

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/documents/mine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyDocuments(response.data);
    } catch (err) {}
  };

  const fetchResources = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/resources`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResources(response.data);
    } catch (err) {}
  };

  const fetchSpeakersAndSessions = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [spkRes, sesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/speakers`, { headers }),
        axios.get(`${BACKEND_URL}/api/sessions`, { headers }),
      ]);
      setSpeakersLocked(spkRes.data.locked);
      setProgramLocked(sesRes.data.locked);
      if (!spkRes.data.locked) setApiSpeakers(spkRes.data.speakers);
      if (!sesRes.data.locked) setApiSessions(sesRes.data.sessions);
    } catch (err) {}
  };

  const handlePayStripe = async () => {
    setPaymentLoading(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/payment/stripe/create-session`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        setPaymentLoading(false);
        alert(response.data?.message || 'Unable to start Stripe checkout.');
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Stripe service error. Please try again.');
      setPaymentLoading(false);
    }
  };

  const handleVerifyPayment = async () => {
    setVerifyingPayment(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/payment/verify`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.status === 'paid') {
        fetchRegistration();
        fetchSpeakersAndSessions();
      }
    } catch (err) {}
    finally { setVerifyingPayment(false); }
  };

  const markAllRead = async () => {
    try {
      await axios.patch(`${BACKEND_URL}/api/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) {}
  };

  const downloadDoc = async (docId, filename) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {}
  };

  const previewDocInline = async (doc) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/documents/${doc.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPreviewDoc({ id: doc.id, title: doc.title, filename: doc.original_filename, url });
    } catch (err) {
      alert(t.documents.previewError);
    }
  };

  const closePreview = () => {
    if (previewDoc?.url) URL.revokeObjectURL(previewDoc.url);
    setPreviewDoc(null);
  };

  const toggleSession = (sessionId) => {
    setMySessions(prev =>
      prev.includes(sessionId) ? prev.filter(id => id !== sessionId) : [...prev, sessionId]
    );
  };

  const handleLogout = () => { logout(); navigate('/'); };

  const navItems = [
    { id: 'overview', icon: Home, label: t.nav.overview },
    { id: 'schedule', icon: Calendar, label: t.nav.schedule },
    { id: 'speakers', icon: Mic2, label: t.nav.speakers },
    { id: 'venue', icon: Map, label: t.nav.venue },
    { id: 'badge', icon: QrCode, label: t.nav.badge },
    { id: 'resources', icon: BookOpen, label: t.nav.resources },
    { id: 'documents', icon: FileText, label: t.nav.documents, badge: myDocuments.length },
    { id: 'history', icon: Award, label: t.nav.history },
    { id: 'notifications', icon: Bell, label: t.nav.notifications, badge: unreadCount },
    // Partner gets an extra "Partner Portal" tab inside their regular dashboard.
    ...(user?.role === 'partner' ? [{ id: 'partner', icon: Sparkles, label: t.nav.partner, accent: true }] : []),
  ];

  // Badge download (must be at top-level — before any early return)
  const badgeRef = React.useRef(null);
  const [downloadingBadge, setDownloadingBadge] = useState(false);

  const handleDownloadBadge = async () => {
    if (!badgeRef.current) return;
    setDownloadingBadge(true);
    try {
      const dataUrl = await toPng(badgeRef.current, {
        cacheBust: true,
        pixelRatio: 3,
        backgroundColor: '#ffffff',
      });
      const link = document.createElement('a');
      link.download = `badge-TK2027-${(user?.last_name || 'attendee').replace(/\s+/g, '_')}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Badge export failed', err);
    } finally {
      setDownloadingBadge(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafafa]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#CD0000]"></div>
      </div>
    );
  }

  // ========== OVERVIEW TAB ==========
  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Conférence Hero Banner */}
      <div className="relative rounded-2xl overflow-hidden h-48 md:h-56">
        <img
          src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/n2geeql4_file_00000000e4dc722f93e14f7e2b511ce8.png"
          alt="Conférence"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-center">
          <p className="text-[#CD0000] font-bold text-sm tracking-widest mb-1">{t.overview.conférenceDate}</p>
          <h2 className="text-white text-xl md:text-2xl font-extrabold leading-tight mb-2">{t.overview.conférenceTitle}</h2>
          <p className="text-white/70 text-xs md:text-sm max-w-lg">{t.overview.conférenceTheme}</p>
          <div className="flex items-center gap-2 mt-3 text-white/80 text-xs">
            <MapPin className="w-3.5 h-3.5" />
            <span>{t.overview.conférenceVenue}</span>
          </div>
        </div>
      </div>

      {/* Countdown */}
      <div data-testid="dashboard-countdown" className="bg-[#1D1D1D] rounded-2xl p-6">
        <p className="text-white/60 text-sm text-center mb-4 tracking-wide">{t.overview.countdown}</p>
        <div className="grid grid-cols-4 gap-3 max-w-md mx-auto">
          {[
            { val: countdown.days, label: t.overview.days },
            { val: countdown.hours, label: t.overview.hours },
            { val: countdown.minutes, label: t.overview.minutes },
            { val: countdown.seconds, label: t.overview.seconds },
          ].map((item, i) => (
            <div key={i} className="text-center">
              <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                <span className="text-white text-2xl md:text-3xl font-extrabold tabular-nums">
                  {String(item.val).padStart(2, '0')}
                </span>
              </div>
              <p className="text-white/50 text-xs mt-2 uppercase tracking-wider">{item.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Registration Status */}
      <div data-testid="dashboard-registration-card" className="bg-white rounded-2xl border border-gray-100 p-6">
        <h3 className="font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#D4AF37]" />
          {t.overview.regStatus}
        </h3>
        {registration ? (
          <div>
            <div className="flex items-center justify-between">
              <p className="text-gray-600 text-sm">
                {registration.status === 'paid' ? t.overview.paidMsg :
                 registration.status === 'approved' ? t.overview.approvedMsg :
                 registration.status === 'incomplete' ? t.overview.incompleteMsg :
                 registration.status === 'rejected' ? t.overview.rejectedMsg : t.overview.pendingMsg}
              </p>
              <span data-testid="dashboard-registration-status" className={`px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap ml-4 ${
                registration.status === 'paid' ? 'bg-emerald-100 text-emerald-800' :
                registration.status === 'approved' ? 'bg-green-100 text-green-800' :
                registration.status === 'incomplete' ? 'bg-orange-100 text-orange-800' :
                registration.status === 'rejected' ? 'bg-red-100 text-red-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>
                {registration.status === 'paid' ? t.overview.paid :
                 registration.status === 'approved' ? t.overview.approved :
                 registration.status === 'incomplete' ? t.overview.incomplete :
                 registration.status === 'rejected' ? t.overview.rejected : t.overview.pending}
              </span>
            </div>
            {registration.status === 'incomplete' && registration.admin_note && (
              <div className="mt-3 bg-orange-50 border border-orange-200 rounded-xl p-4">
                <p className="text-xs font-bold text-orange-600 uppercase tracking-wide mb-1">{t.overview.adminNote}</p>
                <p className="text-sm text-orange-800">{registration.admin_note}</p>
                <Link
                  to="/conference-registration?edit=1"
                  data-testid="edit-resubmit-button"
                  className="mt-3 inline-flex items-center gap-2 bg-orange-600 text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-orange-700 transition-colors"
                >
                  {t.overview.editResubmit}
                </Link>
              </div>
            )}
            {registration.status === 'approved' && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={handlePayStripe}
                    disabled={paymentLoading}
                    data-testid="pay-stripe-button"
                    className="bg-[#CD0000] text-white px-6 py-3 rounded-xl font-bold text-sm hover:bg-[#c91560] transition-all disabled:opacity-50 animate-heartbeat flex items-center gap-2"
                  >
                    {paymentLoading ? t.overview.payingMsg : t.overview.payStripe}
                  </button>
                  <button
                    onClick={handleVerifyPayment}
                    disabled={verifyingPayment}
                    data-testid="verify-payment-button"
                    className="bg-gray-100 text-gray-700 px-4 py-3 rounded-xl font-bold text-xs hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    {verifyingPayment ? '...' : t.overview.verifyPayment}
                  </button>
                </div>

                {/* DRC prepaid card notice — shown for users who may need it */}
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="drc-prepaid-notice">
                  <p className="text-[13px] text-amber-900 leading-relaxed">
                    <span className="font-bold">🇨🇩 {language === 'fr' ? 'Participants de la RDC :' : 'DRC participants:'}</span>{' '}
                    {language === 'fr'
                      ? "Vous pouvez obtenir une carte Visa prépayée dans les banques suivantes pour effectuer votre paiement : "
                      : 'You can obtain a prepaid Visa card at the following banks to complete your payment: '}
                    <span className="font-semibold">FirstBank · FINCA · TMB · Ecobank</span>.
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <p className="text-gray-500 text-sm">{t.overview.notRegistered}</p>
            <Link
              to="/conference-registration"
              data-testid="dashboard-register-conférence-button"
              className="bg-[#CD0000] text-white px-5 py-2.5 rounded-xl font-bold text-sm hover:bg-[#c91560] transition-colors animate-heartbeat"
            >
              {t.overview.registerNow}
            </Link>
          </div>
        )}
      </div>

      {/* Share Flyer Widget — viral ambassador loop */}
      <ShareFlyerWidget language={language} />

      {/* Quick Actions + Announcements Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h3 className="font-bold text-[#1D1D1D] mb-4">{t.overview.quickActions}</h3>
          <div className="space-y-3">
            {[
              { icon: Calendar, label: t.overview.viewSchedule, action: () => setActiveTab('schedule'), color: 'text-[#CD0000]' },
              { icon: Download, label: t.overview.downloadApp, action: () => setActiveTab('resources'), color: 'text-[#D4AF37]' },
              { icon: MessageSquare, label: t.overview.contactOrg, action: () => window.open('mailto:info@talithakoumitug.com'), color: 'text-blue-600' },
            ].map((item, i) => (
              <button
                key={i}
                onClick={item.action}
                data-testid={`quick-action-${i}`}
                className="w-full flex items-center justify-between p-3 rounded-xl hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl bg-gray-50 group-hover:bg-white flex items-center justify-center ${item.color}`}>
                    <item.icon className="w-5 h-5" />
                  </div>
                  <span className="font-medium text-sm text-[#1D1D1D]">{item.label}</span>
                </div>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </button>
            ))}
          </div>
        </div>

        {/* Announcements */}
        <div className="bg-white rounded-2xl border border-gray-100 p-6">
          <h3 className="font-bold text-[#1D1D1D] mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-[#CD0000]" />
            {t.overview.announcements}
          </h3>
          <div className="space-y-3">
            {announcementsData.map((a) => (
              <div key={a.id} className={`p-3 rounded-xl border text-sm ${
                a.type === 'success' ? 'bg-green-50 border-green-100' :
                a.type === 'promo' ? 'bg-[#CD0000]/5 border-[#CD0000]/10' :
                'bg-blue-50 border-blue-100'
              }`}>
                <p className="text-gray-800">{a.text[language] || a.text.fr}</p>
                <p className="text-gray-400 text-xs mt-1">{new Date(a.date).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // ========== SCHEDULE TAB ==========
  const ScheduleTab = () => {
    if (programLocked) {
      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-extrabold text-[#1D1D1D]">{t.schedule.title}</h2>
            <p className="text-gray-500 text-sm mt-1">{t.schedule.subtitle}</p>
          </div>
          <div data-testid="program-locked-message" className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-extrabold text-[#1D1D1D] mb-2">{t.schedule.title}</h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto">{t.schedule.locked}</p>
          </div>
        </div>
      );
    }

    // Use API sessions if available, otherwise fall back to hardcoded data
    const useApiSessions = apiSessions.length > 0;
    const sessionsByDay = useApiSessions
      ? [1, 2, 3].map(d => apiSessions.filter(s => s.day === d).map(s => ({
          ...s, title: s.title, icon: getSessionIcon(s.session_type), type: s.session_type
        })))
      : scheduleData;
    const allSessions = useApiSessions ? sessionsByDay.flat() : scheduleData.flat();
    const dayLabels = [t.schedule.day1, t.schedule.day2, t.schedule.day3];
    const currentDaySessions = sessionsByDay[activeDay] || [];
    const mySessionsList = allSessions.filter(s => mySessions.includes(s.id));

    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-extrabold text-[#1D1D1D]">{t.schedule.title}</h2>
          <p className="text-gray-500 text-sm mt-1">{t.schedule.subtitle}</p>
        </div>

        {/* My Schedule Toggle */}
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={() => setShowMySchedule(false)}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors ${!showMySchedule ? 'bg-[#1D1D1D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            {t.schedule.title}
          </button>
          <button
            onClick={() => setShowMySchedule(true)}
            data-testid="my-schedule-toggle"
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-colors flex items-center gap-2 ${showMySchedule ? 'bg-[#CD0000] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            <Star className="w-4 h-4" />
            {t.schedule.mySchedule} ({mySessions.length})
          </button>
        </div>

        {showMySchedule ? (
          <div className="space-y-3">
            {mySessionsList.length === 0 ? (
              <div className="bg-white rounded-2xl border border-gray-100 p-8 text-center">
                <Star className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500 text-sm">{t.schedule.noSessions}</p>
              </div>
            ) : (
              mySessionsList.map((session) => (
                <SessionCard key={session.id} session={session} />
              ))
            )}
          </div>
        ) : (
          <>
            {/* Day Tabs */}
            <div className="flex gap-2">
              {dayLabels.map((label, i) => (
                <button
                  key={i}
                  onClick={() => setActiveDay(i)}
                  data-testid={`schedule-day-${i+1}`}
                  className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-colors ${
                    activeDay === i ? 'bg-[#D4AF37] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Sessions */}
            <div className="space-y-3">
              {currentDaySessions.map((session) => (
                <SessionCard key={session.id} session={session} />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  const SessionCard = ({ session }) => {
    const isAdded = mySessions.includes(session.id);
    const Icon = session.icon || getSessionIcon(session.type || session.session_type);
    const sType = session.type || session.session_type || 'general';
    const sTitle = typeof session.title === 'object' ? (session.title[language] || session.title.fr) : session.title;
    return (
      <div className={`bg-white rounded-2xl border p-4 transition-all hover:shadow-md ${isAdded ? 'border-[#D4AF37]/40 ring-1 ring-[#D4AF37]/20' : 'border-gray-100'}`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${typeColors[sType] || typeColors.general}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold text-gray-400">{session.time}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${typeColors[sType] || typeColors.general}`}>
                  {sType}
                </span>
              </div>
              <h4 className="font-bold text-[#1D1D1D] text-sm leading-snug">{sTitle}</h4>
              {session.speaker && (
                <p className="text-gray-500 text-xs mt-1">{session.speaker}</p>
              )}
            </div>
          </div>
          {sType !== 'general' && (
            <button
              onClick={() => toggleSession(session.id)}
              data-testid={`session-toggle-${session.id}`}
              className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                isAdded
                  ? 'bg-[#D4AF37] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37]'
              }`}
            >
              {isAdded ? <><CheckCircle className="w-3.5 h-3.5 inline mr-1" />{t.schedule.added}</> : <>+ {t.schedule.addToSchedule}</>}
            </button>
          )}
        </div>
      </div>
    );
  };

  // ========== SPEAKERS TAB ==========
  // ========== SPEAKERS TAB ==========
  const SpeakersTab = () => {
    if (speakersLocked) {
      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-extrabold text-[#1D1D1D]">{t.speakers.title}</h2>
            <p className="text-gray-500 text-sm mt-1">{t.speakers.subtitle}</p>
          </div>
          <div data-testid="speakers-locked-message" className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-extrabold text-[#1D1D1D] mb-2">{t.speakers.title}</h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto">{t.speakers.locked}</p>
          </div>
        </div>
      );
    }
    const displaySpeakers = apiSpeakers.length > 0 ? apiSpeakers : speakersData;
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-extrabold text-[#1D1D1D]">{t.speakers.title}</h2>
          <p className="text-gray-500 text-sm mt-1">{t.speakers.subtitle}</p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {displaySpeakers.map((speaker, i) => (
            <div key={speaker.id || i} data-testid={`speaker-card-${i}`} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow">
              <div className="flex">
                {speaker.image && <img src={speaker.image} alt={speaker.name} className="w-28 h-36 object-cover" />}
                <div className="p-4 flex-1">
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold bg-[#CD0000]/10 text-[#CD0000]">
                    {speaker.role?.[language] || speaker.role?.fr || ''}
                  </span>
                  <h3 className="font-extrabold text-[#1D1D1D] mt-2 text-sm">{speaker.name}</h3>
                  <p className="text-gray-500 text-xs mt-1 leading-relaxed line-clamp-2">{speaker.bio?.[language] || speaker.bio?.fr || ''}</p>
                  <p className="text-[#CD0000] text-xs font-bold mt-2">{speaker.topic?.[language] || speaker.topic?.fr || ''}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ========== VENUE TAB ==========
  const VenueTab = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-extrabold text-[#1D1D1D]">{t.venue.title}</h2>
        <p className="text-gray-500 text-sm mt-1">{t.venue.subtitle}</p>
      </div>

      {/* Venue Image */}
      <div className="relative rounded-2xl overflow-hidden h-52">
        <img src="https://images.unsplash.com/photo-1728486890783-468838bb1be3?w=1200&h=400&fit=crop" alt="Venue" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <h3 className="text-white text-xl font-extrabold">{t.venue.hotelName}</h3>
          <p className="text-white/80 text-sm flex items-center gap-1 mt-1">
            <MapPin className="w-4 h-4" />{t.venue.address}
          </p>
        </div>
      </div>

      {/* WiFi Card */}
      <div className="bg-[#1D1D1D] rounded-2xl p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Wifi className="w-6 h-6 text-[#D4AF37]" />
          <div>
            <p className="text-white font-bold text-sm">WiFi: {t.venue.wifiName}</p>
            <p className="text-white/50 text-xs">Password: {t.venue.wifiPassword}</p>
          </div>
        </div>
        <button
          onClick={() => navigator.clipboard?.writeText(t.venue.wifiPassword)}
          className="text-[#D4AF37] text-xs font-bold hover:underline"
        >
          Copy
        </button>
      </div>

      {/* Info Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {[
          { icon: Map, title: t.venue.directions, text: t.venue.directionsText, color: 'text-blue-600' },
          { icon: MapPin, title: t.venue.parking, text: t.venue.parkingText, color: 'text-amber-600' },
          { icon: Coffee, title: t.venue.nearby, text: t.venue.nearbyText, color: 'text-[#D4AF37]' },
        ].map((item, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-3">
              <item.icon className={`w-5 h-5 ${item.color}`} />
              <h4 className="font-bold text-[#1D1D1D] text-sm">{item.title}</h4>
            </div>
            <p className="text-gray-500 text-xs leading-relaxed">{item.text}</p>
          </div>
        ))}
      </div>

      {/* Accommodation / Hotel Booking */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="bg-[#CD0000]/5 border-b border-[#CD0000]/10 p-5">
          <div className="flex items-center gap-2 mb-1">
            <Home className="w-5 h-5 text-[#CD0000]" />
            <h4 className="font-extrabold text-[#1D1D1D] text-sm">{t.venue.accommodation}</h4>
          </div>
          <p className="text-gray-500 text-xs">{t.venue.accommodationText}</p>
        </div>
        <div className="p-5">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
            <div className="flex-1 space-y-2">
              <div className="flex items-center gap-3">
                <div className="bg-[#1D1D1D] text-white text-xs font-bold px-3 py-1.5 rounded-lg">
                  {t.venue.hotelName}
                </div>
                <span className="text-[#CD0000] font-extrabold text-sm">{t.venue.accommodationRate}</span>
                <span className="text-gray-500 text-xs font-medium ml-1">{t.venue.accommodationRateCad}</span>
              </div>
              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {t.venue.accommodationEventDates}</span>
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-amber-500" /> {t.venue.accommodationDeadline}</span>
              </div>
            </div>
          </div>
          <a
            href="https://app.marriott.com/reslink?id=1775674397414&key=GRP&app=resvlink"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hotel-booking-link"
            className="w-full block text-center bg-[#CD0000] text-white py-3 rounded-xl font-bold text-sm hover:bg-[#c91560] transition-colors"
          >
            <ExternalLink className="w-4 h-4 inline mr-2" />{t.venue.accommodationBook}
          </a>
        </div>
      </div>
    </div>
  );

  // ========== BADGE TAB ==========
  const BadgeTab = () => {
    const badgeId = user ? `TK2027-${user.id?.slice(0, 8).toUpperCase()}` : 'TK2027-XXXX';
    const photoSrc = user?.photo_url
      ? (user.photo_url.startsWith('http') ? user.photo_url : `${BACKEND_URL}${user.photo_url}`)
      : '';
    return (
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-extrabold text-[#1D1D1D]">{t.badge.title}</h2>
            <p className="text-gray-500 text-sm mt-1">{t.badge.subtitle}</p>
          </div>
          <button
            type="button"
            onClick={handleDownloadBadge}
            disabled={downloadingBadge}
            data-testid="download-badge-btn"
            className="inline-flex items-center gap-2 bg-[#1D1D1D] hover:bg-black disabled:opacity-60 text-white font-bold text-sm px-4 py-2.5 rounded-xl transition"
          >
            <Download className="w-4 h-4" />
            {downloadingBadge ? '…' : t.badge.downloadBadge}
          </button>
        </div>

        <div className="flex justify-center">
          <div
            ref={badgeRef}
            data-testid="conférence-badge"
            className="bg-white rounded-3xl border-2 border-gray-200 overflow-hidden w-full max-w-sm shadow-xl"
          >
            {/* Badge Header */}
            <div className="bg-[#1D1D1D] p-5 text-center">
              <img
                src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/xsd39lyf_IMG-20260121-WA0012.jpg"
                alt="Logo"
                className="h-14 mx-auto mb-2"
                crossOrigin="anonymous"
              />
              <p className="text-[#CD0000] font-extrabold text-xs tracking-widest">{t.overview.conférenceTitle}</p>
              <p className="text-white/50 text-xs mt-1">{t.overview.conférenceDate}</p>
            </div>

            {/* Badge Body */}
            <div className="p-6 text-center">
              {photoSrc ? (
                <img
                  src={photoSrc}
                  alt={`${user?.first_name || ''} ${user?.last_name || ''}`.trim()}
                  data-testid="badge-user-photo"
                  crossOrigin="anonymous"
                  className="w-24 h-24 rounded-full object-cover mx-auto mb-3 border-4 border-[#CD0000]/20 shadow-md"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-3 border-4 border-[#D4AF37]/10">
                  <User className="w-12 h-12 text-[#D4AF37]" />
                </div>
              )}
              <h3 className="font-extrabold text-xl text-[#1D1D1D]">
                {user?.first_name} {user?.last_name}
              </h3>
              <p className="text-[#CD0000] font-bold text-xs tracking-widest mt-1">{t.badge.attendee}</p>

              {/* QR Code */}
              <div className="mt-5 bg-white p-4 rounded-2xl border border-gray-100 inline-block">
                <QRCodeSVG
                  value={JSON.stringify({ id: badgeId, name: `${user?.first_name} ${user?.last_name}`, email: user?.email, conf: 'TALITHA-OTTAWA-2027' })}
                  size={160}
                  level="M"
                  fgColor="#1D1D1D"
                />
              </div>

              <p className="text-gray-400 text-xs mt-3">{t.badge.scanInfo}</p>

              {/* Conférence ID */}
              <div className="mt-4 bg-gray-50 rounded-xl p-3">
                <p className="text-gray-400 text-xs">{t.badge.conférenceId}</p>
                <p className="font-mono font-bold text-[#1D1D1D] tracking-wider">{badgeId}</p>
              </div>
            </div>

            {/* Badge Footer */}
            <div className="bg-[#D4AF37] p-3 text-center">
              <p className="text-white text-xs font-bold">{t.overview.conférenceVenue}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ========== RESOURCES TAB ==========
  const ResourcesTab = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-extrabold text-[#1D1D1D]">{t.resources.title}</h2>
        <p className="text-gray-500 text-sm mt-1">{t.resources.subtitle}</p>
      </div>
      {resources.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {resources.map((res, i) => (
            <div key={res.id} data-testid={`resource-card-${i}`} className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3 bg-[#CD0000]/10 text-[#CD0000]">
                <FileText className="w-6 h-6" />
              </div>
              <h4 className="font-bold text-[#1D1D1D] text-sm mb-1">{res.title}</h4>
              <p className="text-gray-500 text-xs leading-relaxed mb-1">{res.description}</p>
              <p className="text-gray-400 text-xs mb-4">{res.original_filename} ({Math.round(res.size / 1024)}KB)</p>
              <button
                onClick={() => downloadDoc(res.id, res.original_filename)}
                data-testid={`download-resource-${res.id}`}
                className="w-full py-2.5 rounded-xl text-xs font-bold bg-[#1D1D1D] text-white hover:bg-[#333] transition-colors"
              >
                <Download className="w-3.5 h-3.5 inline mr-1" />{t.resources.download}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">{t.resources.noResources}</p>
        </div>
      )}
    </div>
  );

  // ========== DOCUMENTS TAB ==========
  const handleDownloadProgram = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/program/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'TALITHA-KOUMI-TUG-Programme-Mars-2027.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Download failed');
    }
  };

  const DocumentsTab = () => (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold text-[#1D1D1D]">{t.documents.title}</h2>
          <p className="text-gray-500 text-sm mt-1">{t.documents.subtitle}</p>
        </div>
        <button
          onClick={handleDownloadProgram}
          data-testid="user-download-program-btn"
          className="px-4 py-2.5 rounded-xl text-sm font-bold bg-[#D4AF37] hover:bg-amber-500 text-black transition-colors flex items-center gap-2 shadow-sm"
        >
          <FileText className="w-4 h-4" />
          {language === 'fr' ? 'Télécharger le programme (PDF)' : 'Download Program (PDF)'}
        </button>
      </div>
      {myDocuments.length > 0 ? (
        <div className="space-y-3">
          {myDocuments.map((doc, i) => (
            <div key={doc.id} data-testid={`document-card-${i}`} className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 bg-blue-50 text-blue-600">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-[#1D1D1D] text-sm">{doc.title}</h4>
                    {doc.description && <p className="text-gray-500 text-xs mt-1">{doc.description}</p>}
                    <div className="flex items-center gap-3 mt-2">
                      <span className="text-gray-400 text-xs">{doc.original_filename}</span>
                      <span className="text-gray-300">|</span>
                      <span className="text-gray-400 text-xs">{Math.round(doc.size / 1024)}KB</span>
                      <span className="text-gray-300">|</span>
                      <span className="text-gray-400 text-xs">{new Date(doc.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  {doc.content_type === 'application/pdf' && (
                    <button
                      onClick={() => previewDocInline(doc)}
                      data-testid={`preview-document-${doc.id}`}
                      className="px-4 py-2 rounded-xl text-xs font-bold bg-white border border-[#CD0000] text-[#CD0000] hover:bg-[#CD0000] hover:text-white transition-colors flex items-center gap-1"
                    >
                      <Eye className="w-3.5 h-3.5" />{t.documents.preview}
                    </button>
                  )}
                  <button
                    onClick={() => downloadDoc(doc.id, doc.original_filename)}
                    data-testid={`download-document-${doc.id}`}
                    className="px-4 py-2 rounded-xl text-xs font-bold bg-[#D4AF37] text-white hover:bg-[#4d8a29] transition-colors flex items-center gap-1"
                  >
                    <Download className="w-3.5 h-3.5" />{t.documents.download}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
          <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">{t.documents.noDocuments}</p>
        </div>
      )}
    </div>
  );

  // ========== NOTIFICATIONS TAB ==========
  const NotificationsTab = () => {
    useEffect(() => { fetchNotifications(); }, []);
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-extrabold text-[#1D1D1D]">{t.notifications.title}</h2>
            <p className="text-gray-500 text-sm mt-1">{t.notifications.subtitle}</p>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              data-testid="mark-all-read-btn"
              className="px-4 py-2 rounded-xl text-xs font-bold bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
            >
              <CheckCircle className="w-3.5 h-3.5 inline mr-1" />{t.notifications.markAllRead}
            </button>
          )}
        </div>
        {notifications.length > 0 ? (
          <div className="space-y-3">
            {notifications.map((notif, i) => (
              <div
                key={notif.id}
                data-testid={`notification-${i}`}
                className={`bg-white rounded-2xl border p-5 transition-all ${!notif.is_read ? 'border-[#CD0000]/30 ring-1 ring-[#CD0000]/10' : 'border-gray-100'}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    notif.type === 'document' ? 'bg-blue-50 text-blue-600' : 'bg-[#D4AF37]/10 text-[#D4AF37]'
                  }`}>
                    {notif.type === 'document' ? <FileText className="w-5 h-5" /> : <BookOpen className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-[#1D1D1D] text-sm">{notif.title}</h4>
                      {!notif.is_read && (
                        <span className="bg-[#CD0000] text-white text-xs px-2 py-0.5 rounded-full font-bold">{t.notifications.new}</span>
                      )}
                    </div>
                    <p className="text-gray-500 text-xs mt-1">{notif.message}</p>
                    <p className="text-gray-400 text-xs mt-2">{new Date(notif.created_at).toLocaleString()}</p>
                  </div>
                  {notif.related_doc_id && (
                    <button
                      onClick={() => {
                        if (notif.type === 'document') setActiveTab('documents');
                        else setActiveTab('resources');
                      }}
                      className="shrink-0 text-[#CD0000] text-xs font-bold hover:underline"
                    >
                      <ChevronRight className="w-4 h-4 inline" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">{t.notifications.noNotifications}</p>
          </div>
        )}
      </div>
    );
  };

  // ========== HISTORY TAB ==========
  const HistoryTab = () => {
    useEffect(() => { fetchHistory(); }, []);
    const histLabels = language === 'fr'
      ? { title: "Historique de Participation", subtitle: "Tous les événements auxquels vous êtes inscrit(e) ou avez participé.", none: "Vous n'avez encore participé à aucun événement.", attended: "Participé", registered: "Inscrit(e)", paid: "Payé", approved: "Approuvé", pending: "En attente", rejected: "Rejeté", incomplete: "Incomplet", exempted: "Exempté", upcoming: "À venir", past: "Passé", invitation: "Invitation", dateReg: "Inscrit(e) le", downloadCert: "Télécharger le Certificat", certError: "Impossible de télécharger le certificat" }
      : { title: "Participation History", subtitle: "All events you are registered for or have attended.", none: "You haven't participated in any event yet.", attended: "Attended", registered: "Registered", paid: "Paid", approved: "Approved", pending: "Pending", rejected: "Rejected", incomplete: "Incomplete", exempted: "Exempted", upcoming: "Upcoming", past: "Past", invitation: "Invitation", dateReg: "Registered on", downloadCert: "Download Certificate", certError: "Could not download certificate" };

    const downloadCertificate = async (regId) => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/participations/${regId}/certificate`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `Attendance_Certificate.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        alert(histLabels.certError);
      }
    };

    const statusBadge = (h) => {
      const map = {
        paid: { txt: histLabels.attended && h.is_past_event ? histLabels.attended : histLabels.paid, css: "bg-emerald-100 text-emerald-800" },
        approved: { txt: histLabels.approved, css: "bg-green-100 text-green-800" },
        pending: { txt: histLabels.pending, css: "bg-yellow-100 text-yellow-800" },
        rejected: { txt: histLabels.rejected, css: "bg-red-100 text-red-800" },
        incomplete: { txt: histLabels.incomplete, css: "bg-orange-100 text-orange-800" },
      };
      return map[h.status] || { txt: h.status, css: "bg-gray-100 text-gray-700" };
    };
    return (
      <div data-testid="history-tab" className="space-y-6">
        <div>
          <h2 className="text-xl font-extrabold text-[#1D1D1D]">{histLabels.title}</h2>
          <p className="text-gray-500 text-sm mt-1">{histLabels.subtitle}</p>
        </div>
        {!historyData && (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center text-gray-400 text-sm">…</div>
        )}
        {historyData && historyData.history.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
            <Award className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">{histLabels.none}</p>
          </div>
        )}
        {historyData && historyData.history.length > 0 && (
          <div className="space-y-3">
            {historyData.history.map((h) => {
              const badge = statusBadge(h);
              const title = language === 'fr' ? (h.event_title_fr || h.event_title) : h.event_title;
              return (
                <div key={h.registration_id} data-testid={`history-item-${h.registration_id}`} className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-[#CD0000]/40 transition-colors">
                  <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${h.is_past_event ? 'bg-gray-100' : 'bg-[#CD0000]/10'}`}>
                        <Award className={`w-5 h-5 ${h.is_past_event ? 'text-gray-500' : 'text-[#CD0000]'}`} />
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-bold text-[#1D1D1D] truncate">{title}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">{h.start_date} → {h.end_date} {h.venue ? ` · ${h.venue}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1.5 shrink-0">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${badge.css}`}>{badge.txt}</span>
                      {h.payment_exempted && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-purple-50 text-purple-700 border border-purple-200">{histLabels.exempted}</span>
                      )}
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${h.is_past_event ? 'bg-gray-100 text-gray-600' : 'bg-sky-50 text-sky-700 border border-sky-200'}`}>{h.is_past_event ? histLabels.past : histLabels.upcoming}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                    {h.created_at && (
                      <p className="text-xs text-gray-400">{histLabels.dateReg}: {new Date(h.created_at).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-CA')}</p>
                    )}
                    {h.attended && h.is_past_event && (
                      <button
                        onClick={() => downloadCertificate(h.registration_id)}
                        data-testid={`download-certificate-${h.registration_id}`}
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-[#CD0000] to-[#A00000] hover:shadow-lg text-white font-bold text-xs px-3.5 py-2 rounded-xl transition-all"
                      >
                        <Download className="w-3.5 h-3.5" /> {histLabels.downloadCert}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'schedule': return <ScheduleTab />;
      case 'speakers': return <SpeakersTab />;
      case 'venue': return <VenueTab />;
      case 'badge': return <BadgeTab />;
      case 'resources': return <ResourcesTab />;
      case 'documents': return <DocumentsTab />;
      case 'notifications': return <NotificationsTab />;
      case 'history': return <HistoryTab />;
      case 'partner':
        return user?.role === 'partner'
          ? <PartnerPortalSection user={user} token={token} language={language} />
          : <OverviewTab />;
      default: return <OverviewTab />;
    }
  };

  return (
    <div data-testid="user-dashboard" className="min-h-screen bg-[#fafafa]">
      {/* Top Header */}
      <div className="bg-[#1D1D1D] py-3 px-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_talitha-koumi/artifacts/xsd39lyf_IMG-20260121-WA0012.jpg"
              alt="TALITHA KOUMI TUG"
              className="h-10 w-auto"
            />
            <span className="text-white/60 text-xs font-bold tracking-widest hidden md:inline">CONFERENCE 2027</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-white text-sm font-medium hidden sm:inline">
              {user?.first_name} {user?.last_name}
            </span>
            <button
              onClick={() => setEditProfileOpen(true)}
              data-testid="dashboard-edit-profile-button"
              className="text-white/60 hover:text-white flex items-center gap-1 text-sm transition-colors"
              title={t.editProfile}
            >
              <UserCog className="w-4 h-4" />
              <span className="hidden md:inline">{t.editProfile}</span>
            </button>
            <button
              onClick={handleLogout}
              data-testid="dashboard-logout-button"
              className="text-white/60 hover:text-white flex items-center gap-1 text-sm transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t.logout}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto flex">
        {/* Sidebar */}
        <aside className="w-16 md:w-56 shrink-0 bg-white border-r border-gray-100 min-h-[calc(100vh-52px)] sticky top-0 pt-6">
          <div className="hidden md:block px-5 mb-6">
            <p className="text-gray-400 text-xs">{t.overview.welcome},</p>
            <p className="font-extrabold text-[#1D1D1D] truncate">{user?.first_name}</p>
          </div>
          <nav className="space-y-1 px-2 md:px-3">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  if (item.id === 'notifications') fetchNotifications();
                  if (item.id === 'history') fetchHistory();
                }}
                data-testid={`nav-${item.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all relative ${
                  activeTab === item.id
                    ? (item.accent ? 'bg-[#D4AF37]/15 text-[#8a7223]' : 'bg-[#CD0000]/10 text-[#CD0000]')
                    : (item.accent ? 'text-[#8a7223] hover:bg-[#D4AF37]/10' : 'text-gray-500 hover:bg-gray-50 hover:text-[#1D1D1D]')
                }`}
              >
                <item.icon className="w-5 h-5 shrink-0" />
                <span className="hidden md:inline">{item.label}</span>
                {item.badge > 0 && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#CD0000] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {item.badge > 9 ? '9+' : item.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8 min-w-0">
          {renderContent()}
        </main>
      </div>

      {/* Floating AI Chatbot */}
      <ChatBot language={language} token={token} />

      {/* Edit Profile Modal */}
      <EditProfileModal
        open={editProfileOpen}
        onClose={() => setEditProfileOpen(false)}
        user={user}
        token={token}
        language={language}
        onSaved={(updated) => updateUser({ ...user, ...updated })}
      />

      {/* PDF Preview Modal */}
      {previewDoc && (
        <div
          data-testid="pdf-preview-modal"
          className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
          onClick={closePreview}
        >
          <div
            className="bg-white rounded-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 bg-[#1D1D1D] text-white shrink-0">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="w-5 h-5 text-[#CD0000] shrink-0" />
                <div className="min-w-0">
                  <p className="font-extrabold text-sm truncate">{previewDoc.title}</p>
                  <p className="text-xs text-white/60 truncate">{previewDoc.filename}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => downloadDoc(previewDoc.id, previewDoc.filename)}
                  data-testid="preview-download-btn"
                  className="px-3 py-1.5 rounded-lg text-xs font-bold bg-[#D4AF37] hover:bg-[#4d8a29] flex items-center gap-1.5"
                >
                  <Download className="w-3.5 h-3.5" /> {t.documents.download}
                </button>
                <button
                  onClick={closePreview}
                  data-testid="preview-close-btn"
                  className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center"
                  aria-label="Close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 w-full bg-gray-100 overflow-hidden">
              <iframe
                src={previewDoc.url}
                title={previewDoc.title}
                className="w-full h-full border-0"
                allow="fullscreen"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
